/**
 * For non-declarative behavior of routes
 */
// @ts-ignore
import Config from 'Config';
import URI from 'urijs';

import { goToClassCode } from 'lib/RouteLogic';
import { rootGroupCode } from 'entities/RootGroup';
import * as GroupPromises from 'groups/lib/GroupPromises';
// @ts-ignore
import AuthActions from 'auth/AuthActions';
import CurrentUser from 'auth/CurrentUser';
// @ts-ignore
import OrganizationActions from 'actions/OrganizationActions';
// @ts-ignore
import User from 'entities/User';

import { RouterState, Location, LocationDescriptor } from 'lib/ReactRouterTypes';

// TODO move resolveDeepLinkRedirects into here

// TODO simplify our user state
export function promiseUser(): Promise<User> {
  if (CurrentUser.isSignedIn()) {
    return Promise.resolve(CurrentUser.getUser());
  } else {
    return AuthActions.checkLoggedInAndLoadAppData();
  }
}

// Gets a 'urlOnAuth' parameter from the query string as long as it's within the remind.com domain
export function getUrlOnAuth(loc: Location) {
  const { query } = loc;
  const { urlOnAuth } = query;
  if (urlOnAuth) {
    if (URI(urlOnAuth).hostname() === URI(Config.Environment.R101_FRONTEND_URL).hostname()) {
      return urlOnAuth;
    }
  }

  return null;
}

export function goLandingIfAuthed(
  nextState: RouterState,
  replaceWith: (location: LocationDescriptor) => void
) {
  if (CurrentUser.isSignedIn()) {
    // If we have a url to redirect to outside r101-frontend, and the user is signed in,
    //   just follow the redirect
    const urlOnAuth = getUrlOnAuth(nextState.location);
    if (urlOnAuth) {
      window.location.replace(urlOnAuth);
      return;
    }

    replaceWith('/landing');
  }
}

// @deprecated using in onEnter makes first CSR blank, breaking clean rehydration with SSR
export function goToClassCodeIfAuthed(
  nextState: RouterState,
  replaceWith: (location: LocationDescriptor) => void,
  callback: Function
) {
  promiseUser().then(
    () => {
      goToClassCode(nextState, replaceWith);
      callback();
    },
    () => callback()
  );
}

// This is just to make testing easier
export function hardRedirectTo(redirectTo: string) {
  window.location.href = redirectTo;
}

/*
 Handles redirecting from /classes/:groupCode to:
   classes/:groupCode/home
   classes/:groupCode/messages
   classes/:groupCode/people

 And from:
   classes/:groupCode/settings to /classes/:groupCode/messages/?settings=true
   classes/:groupCode/invite   to /classes/:groupCode/messages/?invite=true
 */
export async function redirectByClassCode(
  groupCode: string,
  user: User,
  userId: number,
  location: Location,
  replaceWith: Function
) {
  const { query } = location;
  const normalizedPath = location.pathname.replace(/\/$/, '');
  const group = await GroupPromises.loadGroupByCode(groupCode);

  if (!group) {
    // attempt to mount join modal if user is not in the specified class
    if (normalizedPath.endsWith(`/${groupCode}`)) {
      replaceWith({ pathname: '/classes', query: { ...query, join: groupCode } });
      return;
    } else {
      replaceWith({ pathname: '/classes', query });
      return;
    }
  }

  if (group.getType() === 'pending') {
    replaceWith({ pathname: `/classes/${groupCode}/pending`, query });
    return;
  }

  const ownedByMe = group.isOwnedByUser(userId);

  const doRedirect =
    normalizedPath.endsWith(`/${groupCode}/settings`) ||
    normalizedPath.endsWith(`/${groupCode}/invite`) ||
    normalizedPath.endsWith(`/${groupCode}`);

  const newQuery = (() => {
    if (normalizedPath.endsWith(`/${groupCode}/settings`)) {
      return { ...query, settings: true };
    } else if (normalizedPath.endsWith(`/${groupCode}/invite`) && ownedByMe) {
      return { ...query, invite: true };
    } else {
      return { ...query };
    }
  })();

  const isAdminRoute =
    normalizedPath.endsWith(`/${groupCode}/home`) ||
    normalizedPath.includes(`/${groupCode}/settings/organization`) ||
    normalizedPath.endsWith(`/${groupCode}/schools`) ||
    normalizedPath.endsWith(`/${groupCode}/statistics`);

  const orgId = group.getStaffClassOrgId();
  const org = orgId ? await OrganizationActions.getOrLoadOrganization(orgId) : undefined;
  const adminConsolePermissions = org && org.get('permissions').adminConsole;
  const seePeopleTab = adminConsolePermissions ? adminConsolePermissions.seePeopleTab : true;
  const seeHomeTab = adminConsolePermissions ? adminConsolePermissions.seeHomeTab : true;

  if (
    groupCode !== rootGroupCode &&
    ownedByMe &&
    group.getMembershipsCount() < 3 &&
    doRedirect &&
    seePeopleTab
  ) {
    // redirect /:groupCode to /people for groups you own with fewer than 3 members
    replaceWith({ pathname: `/classes/${groupCode}/people`, query: newQuery });
  } else if (org) {
    // fetch admin console access if the group is a staff class
    const adminConsoleAccess = adminConsolePermissions
      ? adminConsolePermissions.accessConsole
      : false;
    if (adminConsoleAccess && doRedirect) {
      if (seeHomeTab) {
        // redirect /:groupCode to /home for admins at paid orgs
        replaceWith({ pathname: `/classes/${groupCode}/home`, query: newQuery });
      } else {
        replaceWith({ pathname: `/classes/${groupCode}/messages`, query: newQuery });
      }
    } else if (doRedirect || (!adminConsoleAccess && isAdminRoute)) {
      // redirect /:groupCode and all admin-specific routes to /messages if user doesn't have admin console access
      replaceWith({ pathname: `/classes/${groupCode}/messages`, query: newQuery });
    } else {
      // do not redirect all other cases for a staff class
      replaceWith({ pathname: normalizedPath, query: newQuery });
    }
  } else if (doRedirect || isAdminRoute) {
    // redirect /:groupCode and all admin-specific routes to /messages for non-staff classes
    replaceWith({ pathname: `/classes/${groupCode}/messages`, query: newQuery });
  } else {
    // do not redirect all other cases
    replaceWith({ pathname: normalizedPath, query: newQuery });
  }
}
